import type { Engagement } from "./engagement";
import { PerformanceClock } from "../app/clock";
import { ScheduleTicker } from "../app/schedule-ticker";
import { Engine } from "../engine/engine";
import { MusicalTimeline } from "../music/musical-timeline";
import { Piano } from "../piano/piano";
import { Controler } from "../track/controler/controler";
import { PianoControler } from "../track/controler/piano-controler";
import { Lane } from "../track/lane";
import { LocalTransport, type TransportLoop } from "../track/local-transport";
import { Track } from "../track/track";
import { TrackManager } from "../track/track-manager";
import { Transport } from "../track/transport";
import { tonalityEquals, type Tonality } from "../music/tonality";
import { StudioView as PixiStudioView } from "./view/studio-view";
import { CanvasCovers, same, type StudioSurface } from "./canvas-cover";
import { DEFAULT_KEYBOARD_HEIGHT } from "../piano/horizontal-projection";
import { MINIMAP_WIDTH } from "./view/components/minimap-component";
import { MetronomeComponent } from "./components/metronome";
import { CountInComponent } from "./components/count-in";
import { ContinuousMetronome, type Signature } from "./components/continuous-metronome";
import { SectionTipsComponent } from "./components/section-tips";
import type { LiveLaneMode, RecordZone } from "./view/components/piano-roll-component";
import { PLAYHEAD_DISTANCE_PX } from "./view/playhead-distance";
import { type LaneView } from "./lane-view";
import { SessionManager } from "./session/session-manager";
import { SessionStage, StudioSession } from "./session/studio-session";
import { SongSequence, type SequenceStage } from "./sequence/song-sequence";
import type { KeySpan } from "@/lib/harmony/key-spans";
import { Observable, type Disposable, type ReadonlyObservable } from "./observable";
import { isPhone } from "@/lib/platform";
import type { PerfProfileStore } from "@/lib/perf/perf-profile-store";
import { getPerfProfileStore } from "@/lib/perf/detect-perf-tier";
import {
  DEFAULT_NOTE_EFFECT_STYLE,
  NOTE_EFFECT_STYLES,
  type NoteEffectStyle,
} from "../engine/components/piano-roll/note-effects/types";

export type { NoteEffectStyle };

const NOTE_EFFECT_STYLE_LS_KEY = "virtuosa.noteEffectStyle";

function readPersistedNoteEffectStyle(): NoteEffectStyle {
  if (typeof window === "undefined") {
    return DEFAULT_NOTE_EFFECT_STYLE;
  }

  const raw = window.localStorage.getItem(NOTE_EFFECT_STYLE_LS_KEY);

  if (raw && (NOTE_EFFECT_STYLES as readonly string[]).includes(raw)) {
    return raw as NoteEffectStyle;
  }

  return isPhone() ? PHONE_NOTE_EFFECT_STYLE : DEFAULT_NOTE_EFFECT_STYLE;
}

/**
 * Sur téléphone, le halo sans ses étincelles, par défaut.
 *
 * Les étincelles naissent à une soixantaine par seconde et par note tenue, et
 * vivent plus d'une seconde : avec la pédale et des accords, elles touchent
 * vite leur plafond de mille, recalculées et renvoyées au GPU à chaque image.
 * Sous Android, elles faisaient ramer le studio — et, dans un replay, perdre
 * leurs images aux caméras (2026-09-29). Le halo, lui, n'est que quelques
 * sprites par touche.
 *
 * Un défaut et non une interdiction : le réglage « Halo + particules » reste
 * là, et un choix retenu l'emporte.
 */
const PHONE_NOTE_EFFECT_STYLE: NoteEffectStyle = "halo";

function persistNoteEffectStyle(style: NoteEffectStyle): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(NOTE_EFFECT_STYLE_LS_KEY, style);
}

export type { CountInEvent, CountInListener } from "./components/count-in";
export type { SectionTipsEvent, SectionTipsListener } from "./components/section-tips";
export type { Signature, Division, DivisionId } from "./components/continuous-metronome";

/**
 * Ce que le joueur **regarde** par-dessus le piano-roll, un à la fois.
 * `roll` = pas d'overlay.
 *
 * Ce sont des instruments d'inspection, et rien d'autre : l'atlas harmonique,
 * les accords, le banc d'essai des niveaux. Ce à quoi le joueur est *engagé*
 * est un axe séparé, et c'est `Engagement`.
 *
 * `lab` n'a d'entrée visible qu'en développement.
 *
 * Il y a eu deux vues de plus, et les deux ont disparu pour la même raison :
 * elles n'étaient pas des choses à regarder. `porch` présentait les deux
 * parcours sur un écran à elle, et doublait la fiche du morceau, qui est le
 * point d'entrée de l'apprentissage depuis toujours ; le choix vit maintenant
 * dans la fiche, qui se réorganise sur place (voir `PieceOverviewOverlay`).
 * `level` **était** le parcours harmonique, c'est-à-dire un engagement rangé
 * parmi des onglets : il est passé sur l'axe qui lui correspond, voir
 * `Engagement`.
 */
export type StudioView = "roll" | "harmony" | "chords" | "lab";

export class Studio {
  /** Disposable registry, drained in `destroy()`. Declared first so that field
   *  initializers running top-to-bottom can push into it. */
  private disposables: Disposable[] = [];
  /** La grille du studio vide, tant que c'est elle que porte le transport. */
  private ownTimeline: MusicalTimeline | null = null;

  private pixiView?: PixiStudioView;
  /** La file des montages de vue, voir `createView`. */
  private viewCreation: Promise<void> = Promise.resolve();
  private ctx: AudioContext;
  /**
   * Le registre que la vue Pixi observe.
   *
   * Il reste au studio, du côté présentation : les composants Pixi le tiennent
   * par référence et s'y abonnent, et il n'y a qu'un piano-roll. Ce sont les
   * pistes qui vont et viennent — la session en scène les y publie en entrant,
   * les en retire en sortant.
   */
  private trackManager: TrackManager;
  private ticker: ScheduleTicker;
  private transport: Transport;
  private localTransport: LocalTransport;

  /** Le clavier du joueur. Propre au poste, il survit aux bascules. */
  private pianoControler: PianoControler;

  private sessions: SessionManager;

  /**
   * Les morceaux chargés, à la suite, et celui qui est sous la tête de
   * lecture. Voir `SongSequence` : c'est là que vit désormais « le morceau
   * courant », que la page lit au lieu de le poser.
   */
  private sequence: SongSequence;

  /**
   * La session dans laquelle la séquence a monté ses pistes.
   *
   * Une séance live met une autre session en scène et prend le transport :
   * la séquence est alors suspendue — plus de borne, plus de changement de
   * morceau — jusqu'au retour de la sienne. Sans ça, atteindre la fin d'un
   * morceau pendant une séance aurait monté le suivant dans la session de
   * tout le monde. `null` tant que rien n'a été monté.
   */
  private sequenceHome: StudioSession | null = null;

  /** Désabonnement de `statechange` sur le contexte audio. */
  private unsubscribeAudioState: (() => void) | null = null;

  private _metronome: MetronomeComponent;
  private _countIn: CountInComponent;
  private _continuousMetronome: ContinuousMetronome;
  private _sectionTips: SectionTipsComponent;

  // `null` au démarrage, c'est-à-dire jeu libre : le studio démarre sans
  // morceau, et un parcours ne veut rien dire tant qu'aucune partition n'est
  // chargée. `studio-page` tient l'invariant en repassant à `null` dès que
  // `songControler` s'annule.
  private _engagement = this.register(new Observable<Engagement | null>(null));
  private _view = this.register(new Observable<StudioView>("roll"));

  /**
   * Ce que les calques montés cachent du canvas.
   *
   * Le registre tient les déclarations, l'observable en tient l'union et la
   * pousse à la vue. Deux objets et non un : les déclarations vont et viennent
   * au montage des écrans, mais l'union ne change qu'aux moments qui comptent —
   * c'est elle qui décide de ce que le studio dessine, et la faire varier pour
   * rien réveillerait le rendu à chaque montage.
   */
  private canvasCovers = new CanvasCovers();
  private _hiddenSurfaces = this.register(
    new Observable<ReadonlySet<StudioSurface>>(new Set<StudioSurface>(), {
      equals: same,
      onChange: (hidden) => this.pixiView?.setHiddenSurfaces(hidden),
    }),
  );
  private _tonality = this.register(
    new Observable<Tonality | null>(null, { equals: tonalityEquals }),
  );

  /** Tempo currently applied (UI BPM). Drives the transport playback rate. */
  private _bpm = this.register(new Observable<number>(120));
  /** Reference BPM (intrinsic tempo of the loaded piece). */
  private _originalBpm = 120;

  /** Qui décide du tempo en séance ; `null` hors séance. Voir `setTempoProposer`. */
  private tempoProposer: ((bpm: number) => void) | null = null;

  /** Qui décide du chiffrage en séance ; `null` hors séance. Voir `setSignature`. */
  private signatureProposer: ((signature: Signature) => void) | null = null;

  /** General audio volume (0–100). UI value, actual gain wiring is the caller's responsibility for now. */
  private _volume = this.register(new Observable<number>(80));

  /** Whether the master output is muted (UI flag, actual gain wiring is the caller's responsibility). */
  private _muted = this.register(new Observable<boolean>(false));

  /**
   * Live keyboard strip height (px), published by the Pixi view on every
   * (re)layout. React overlays read it (via `usePianoHeight`) to clamp exactly
   * to the top of the keyboard. Seeded with the height at MAX key width so the
   * fallback before the first layout is sane (no flash).
   */
  private _keyboardHeight = this.register(new Observable<number>(DEFAULT_KEYBOARD_HEIGHT));

  /**
   * Largeur courante du rail de minimap (px), publiée par la vue Pixi à chaque
   * (re)layout — elle se resserre sur un canvas étroit. Les overlays React la
   * lisent (via `useMinimapWidth`) pour s'arrêter au bord du rail. Amorcée à
   * la largeur des écrans larges, le cas courant avant le premier layout.
   */
  private _minimapWidth = this.register(new Observable<number>(MINIMAP_WIDTH));

  /**
   * Distance courante (px) entre la playhead et le bas du roll, publiée par la
   * vue Pixi à chaque (re)layout — elle redescend vers le piano quand le roll
   * manque de hauteur. Les overlays React qui réservent un couloir de lecture
   * au-dessus de la playhead la lisent (via `usePlayheadDistance`). Amorcée à
   * la distance nominale, le cas des écrans hauts, avant le premier layout.
   */
  private _playheadDistance = this.register(new Observable<number>(PLAYHEAD_DISTANCE_PX));

  /**
   * La zone d'enregistrement, posée dans la règle du roll.
   *
   * Un repérage de séance : il vit en mémoire, et rien ne l'écrit — ni le
   * navigateur, ni le serveur. `null` tant que rien n'a été posé, et
   * `endTick` à `null` entre le premier appui et le second.
   */
  private _recordZone = this.register(new Observable<RecordZone | null>(null));

  /**
   * Le joueur peut-il poser les bornes de la zone dans la règle du roll ?
   *
   * Éteint par défaut, et c'est à l'écran qui voudra enregistrer de l'allumer :
   * partout ailleurs, un appui dans la règle doit rester un geste de
   * navigation. Réappliqué quand une vue est (re)construite.
   */
  private _recordZoneEditable = this.register(new Observable<boolean>(false));

  /** Whether the continuous metronome is enabled (un-muted). */
  private _metronomeEnabled = this.register(new Observable<boolean>(false));

  /**
   * Which scheduler the continuous metronome currently runs, so
   * `applyMetronomeMode` only restarts it when the mode actually changes:
   *  - `free`   : standalone pulse on the Clock, runs while the transport is
   *               paused, lets the metronome click without pressing play.
   *  - `synced` : driven by the LocalTransport, clicks share the playhead grid.
   *  - `stopped`: not running.
   */
  private _metronomeRunMode: "stopped" | "free" | "synced" = "stopped";

  /**
   * True while `startWithCountIn` is driving a count-in. The count-in owns the
   * clicks during that window, so the continuous metronome must stay stopped,
   * even though the transport briefly emits a `pause` event while the count-in
   * arms itself.
   */
  private _countInRunning = false;

  /**
   * Abonnés au changement de mesure, alimentés par la boucle de rendu de la
   * vue. L'émetteur vit ici et non sur `PixiStudioView` parce que la vue est
   * créée en asynchrone (`createView`) : un abonné monté avant elle doit
   * pouvoir s'inscrire quand même.
   */
  private measureListeners = new Set<(measure: number) => void>();
  private frameListeners = new Set<(tick: number) => void>();
  private _currentMeasure = 1;

  /** Unsubscribe from the view's measure feed, drained in `destroy()`. */
  private unsubscribeMeasure: (() => void) | null = null;
  private unsubscribeFrame: (() => void) | null = null;
  private unsubscribeScrub: (() => void) | null = null;
  private unsubscribeRecordZone: (() => void) | null = null;
  /**
   * Notified when the **player** scrolls the roll and lets go.
   *
   * Kept on the Studio rather than handed straight from the view because the
   * view is built asynchronously and rebuilt on a resize, while the surfaces
   * that listen — the steps of the learning journey — outlive both.
   */
  private scrubListeners = new Set<(tick: number) => void>();

  /** Unsubscribe from the loop-wrap feed, drained in `destroy()`. */
  private unsubscribeLoopWrap: (() => void) | null = null;

  /** Unsubscribe from the transport play/pause events, drained in `destroy()`. */
  private unsubscribeTransportEvents: (() => void) | null = null;

  /** Unsubscribe from the section-tips feed, drained in `destroy()`. */
  private unsubscribeSectionTips: (() => void) | null = null;

  /** Side-effecting observable built in the constructor (depends on `pixiView`). */
  private _highlightActiveBeat!: Observable<boolean>;

  /** Passage the learning journey is working on; drawn on the roll. */
  private _workRange: { startTick: number; endTick: number } | null = null;
  private _keySpans: readonly KeySpan[] = [];

  /** How the live-piano lane is drawn; see {@link LiveLaneMode}. */
  private _liveLaneMode: LiveLaneMode = "notes";

  /** La navigation du rouleau est fermée au joueur ; voir `setNavigationLocked`. */
  private _navigationLocked = false;

  /** Visual effect style triggered when a key is pressed (halo / particles / none). */
  private _noteEffectStyle!: Observable<NoteEffectStyle>;
  private _measureLinesVisible!: Observable<boolean>;
  private _footReserve!: Observable<number>;

  /**
   * Le studio a fini son entrée : le carton d'accueil est passé, le jingle
   * aussi. Voir `onReady`.
   */
  private readonly _ready = this.register(new Observable<boolean>(false));

  private readonly perfStore: PerfProfileStore;
  private unsubscribePerf: (() => void) | null = null;

  constructor(private piano: Piano, audioContext: AudioContext, perfStore?: PerfProfileStore) {
    this.ctx = audioContext;
    this.perfStore = perfStore ?? getPerfProfileStore();

    this.ticker = new ScheduleTicker(this.ctx);
    this.ticker.start();

    // La position sur `performance.now()`, l'ordonnancement sur l'horloge du
    // contexte : voir `PositionClock` pour pourquoi les deux sont séparées.
    this.transport = new Transport(new PerformanceClock(), new MusicalTimeline(480));
    this.localTransport = new LocalTransport(this.transport, this.ticker);

    this.pianoControler = new PianoControler(this.piano, this.localTransport);
    this.pianoControler.start();

    // Le registre de présentation avant les sessions : la première entrée en
    // scène y publie déjà sa piste piano.
    this.trackManager = new TrackManager(this.ctx);

    this.sessions = new SessionManager(this.buildSessionStage());
    this.sessions.activate(new StudioSession("Solo"));

    // Après la session : la séquence monte ses pistes dans celle qui est en
    // scène, et il en faut une.
    this.sequence = new SongSequence(this.buildSequenceStage());

    this.watchAudioOutput();

    this._metronome = new MetronomeComponent(this.ctx);
    this._countIn = new CountInComponent(this._metronome, this.transport, this.localTransport, this.ticker);
    this._continuousMetronome = new ContinuousMetronome(this._metronome, this.ticker);
    this._sectionTips = new SectionTipsComponent(this.localTransport);
    this.disposables.push(this._metronome, this._countIn, this._continuousMetronome, this._sectionTips);

    // Sans morceau ni séance, la grille du studio est à lui : la signature
    // choisie au métronome la redessine. Un morceau ou une séance qui a posé
    // la sienne la garde — `ownTimeline` ne la désigne plus.
    //
    // Le tempo du clic suit : ses temps viennent de changer de valeur. Le clic
    // libre en tire sa cadence, le clic calé le tient à jour pour qui le lit.
    this.ownTimeline = this.transport.timeline;
    this._continuousMetronome.signature.subscribe(() => {
      if (this.transport.timeline === this.ownTimeline) {
        this.resetTimeline();
      }

      if (this._metronomeRunMode !== "stopped") {
        this._continuousMetronome.setBpm(this.clickBpm(this._bpm.value));
      }
    });

    // Switch the continuous metronome between its free (paused) and synced
    // (playing) schedulers as playback starts/stops, so the click can run
    // standalone while paused and re-lock onto the playhead grid on play.
    this.unsubscribeLoopWrap = this.localTransport.on((event) => {
      if (event.type === 'loop') {
        void this.countInLoopWrap(event.tick);
      }
    });

    this.unsubscribeTransportEvents = this.localTransport.on((event) => {
      if (event.type === "play" || event.type === "detach") {
        this.applyMetronomeMode();
      }
    });

    /*
      Un interstitiel arrête le clic, et le fait reprendre en repartant.

      Le conseil de section met le transport en pause, et une pause fait
      justement basculer le métronome sur sa pulsation libre : le clic
      continuait donc pendant qu'on lit, à un tempo qui ne mène plus nulle
      part. `applyMetronomeMode` sait maintenant que le rideau compte, encore
      faut-il la rappeler quand il s'ouvre et se ferme — la pause qui
      l'accompagne n'émet rien quand le transport était déjà à l'arrêt, ce qui
      est le cas au tout premier conseil d'un exercice.
    */
    this.unsubscribeSectionTips = this._sectionTips.on(() => {
      this.applyMetronomeMode();
    });

    this._highlightActiveBeat = this.register(
      new Observable<boolean>(false, {
        onChange: (v) => this.pixiView?.setHighlightActiveBeat(v),
      }),
    );

    // The user's note-effect choice is persisted; the *effective* style
    // shown on screen is gated by `perfStore.effects.particlesEnabled` so
    // the auto-downgrade can suppress effects without trashing the user's
    // saved preference.
    this._noteEffectStyle = this.register(
      new Observable<NoteEffectStyle>(readPersistedNoteEffectStyle(), {
        onChange: (v) => {
          this.applyEffectiveNoteEffectStyle();
          persistNoteEffectStyle(v);
        },
      }),
    );

    // Un choix de récital, pas un réglage : non retenu, et remis par la page
    // quand on quitte le récital.
    this._measureLinesVisible = this.register(
      new Observable<boolean>(true, {
        onChange: (v) => this.pixiView?.setMeasureLinesVisible(v),
      }),
    );

    // Mesurée par le DOM et non calculée ici : ce qui occupe le pied du roll
    // est un élément HTML posé dessus. Zéro quand il n'y en a pas, ce qui est
    // le cas de tout le studio hors replay sur téléphone.
    this._footReserve = this.register(
      new Observable<number>(0, {
        onChange: (v) => this.pixiView?.setFootReserve(v),
      }),
    );

    // React to runtime perf changes (auto-downgrade, user override, restore).
    this.unsubscribePerf = this.perfStore.subscribe(() => {
      this.applyEffectiveNoteEffectStyle();
    });
  }

  private applyEffectiveNoteEffectStyle(): void {
    const allowed = this.perfStore.value.effects.particlesEnabled;
    const style: NoteEffectStyle = allowed ? this._noteEffectStyle.value : "none";
    this.pixiView?.setNoteEffectStyle(style);
  }

  private register<T>(obs: Observable<T>): Observable<T> {
    this.disposables.push(obs);

    return obs;
  }

  /**
   * Returns the shared AudioContext owned by the Studio.
   * Used by features that need to schedule audio outside of the Studio's
   * own playback (e.g. the upload-modal pre-listening player). Callers must
   * not close it — its lifecycle is owned by `StudioProvider`.
   */
  getAudioContext(): AudioContext {
    return this.ctx;
  }

  /**
   * Le tampon de sortie **tel que l'ordonnancement l'applique**, en secondes.
   *
   * Passe par le raccord et non par le contexte : `ctx.outputLatency` saute de
   * huit millisecondes d'un relevé à l'autre, et c'est la valeur asservie que
   * `LocalTransport.getTimeFromTick` retranche de chaque note. Annoncer la
   * brute à la séance ferait budgéter au service un nombre que le client
   * n'utilise pas — et, la valeur retenue étant le maximum de la salle, ferait
   * monter le délai de tout le monde au gré du bruit de mesure.
   */
  getOutputLatency(): number {
    return this.ticker.timebase.outputLatencySeconds;
  }

  getPiano(): Piano {
    return this.piano;
  }

  getTrackManager() {
    return this.trackManager;
  }

  getTransport() {
    return this.transport;
  }

  getLocalTransport(): LocalTransport {
    return this.localTransport;
  }

  getLaneView(lane: Lane): LaneView | undefined {
    return this.sessions.getActive()?.getLaneView(lane);
  }

  get metronome(): MetronomeComponent {
    return this._metronome;
  }

  get countIn(): CountInComponent {
    return this._countIn;
  }

  get continuousMetronome(): ContinuousMetronome {
    return this._continuousMetronome;
  }

  get sectionTips(): SectionTipsComponent {
    return this._sectionTips;
  }

  get engagement(): ReadonlyObservable<Engagement | null> {
    return this._engagement;
  }

  setEngagement(engagement: Engagement | null): void {
    this._engagement.set(engagement);
  }

  get view(): ReadonlyObservable<StudioView> {
    return this._view;
  }

  setView(view: StudioView): void {
    this._view.set(view);
  }

  get bpm(): ReadonlyObservable<number> {
    return this._bpm;
  }

  get originalBpm(): number {
    return this._originalBpm;
  }

  /**
   * Update the reference BPM of the loaded piece. Called by `studio-page` whenever a new
   * song is loaded. Resets the active BPM to the original tempo (rate = 1).
   */
  setOriginalBpm(bpm: number): void {
    // En séance, la grille appartient à la séance : ouvrir un morceau ne
    // propose pas son tempo à tout le monde, et ne change pas la vitesse de la
    // vue. Sans cette garde, chaque chargement partait au service comme un
    // geste de tempo. Voir `setTempoProposer`.
    if (this.tempoProposer !== null) {
      return;
    }

    this._originalBpm = bpm;
    this.setBpm(bpm);
  }

  setBpm(bpm: number): void {
    if (this._bpm.value === bpm) {
      return;
    }

    // En séance, le tempo n'est pas un réglage de la vue : c'est la grille de
    // tout le monde. Le geste part au service et revient par l'état publié,
    // qui l'applique ici par `applySessionTempo`. Voir `setTempoProposer`.
    if (this.tempoProposer !== null) {
      // Affiché tout de suite, pour que le curseur ne revienne pas en arrière
      // pendant qu'on le glisse ; la grille, elle, attend l'état publié.
      this._bpm.set(bpm);
      this.tempoProposer(bpm);

      return;
    }

    const rate = this._originalBpm > 0 ? bpm / this._originalBpm : 1;
    this.localTransport.setPlaybackRate(rate);
    // Keep the audible metronome in sync, only meaningful while it's running,
    // setBpm is a cheap no-op when stopped.
    this._continuousMetronome.setBpm(this.clickBpm(bpm));
    this._bpm.set(bpm);
  }

  /**
   * Le tempo du clic, en temps de la grille par minute.
   *
   * Le tempo affiché se compte en noires : c'est le tempo MIDI, celui d'un
   * morceau. Le clic calé sur la lecture, lui, bat les temps de la grille — des
   * croches en 6/8. Le clic libre ne connaît que des battements par minute : à
   * lui passer le tempo affiché, il battait des croches deux fois trop lentes,
   * et doublait en passant à la lecture.
   */
  private clickBpm(bpm: number): number {
    const tick = this.localTransport.currentTick();
    const { denominator } = this.transport.timeline.getTimeSignatureAtTick(tick);

    return bpm * (denominator / 4);
  }

  /**
   * Confie le tempo à une séance, ou le rend à la vue avec `null`.
   *
   * Hors séance, changer de tempo change la vitesse de la vue locale
   * (`setPlaybackRate`), et ne regarde que soi. En séance, deux vitesses ou deux
   * grilles font tomber les notes des autres de plus en plus loin : le tempo
   * devient un fait de séance, proposé au service par ce rappel.
   */
  setTempoProposer(proposer: ((bpm: number) => void) | null): void {
    this.tempoProposer = proposer;
  }

  /**
   * Change le chiffrage, comme on le choisit au métronome.
   *
   * Hors séance, le clic change, et la grille du studio vide le suit (voir le
   * constructeur). En séance, le chiffrage est celui de la grille partagée : il
   * part au service comme le tempo, et revient par l'état publié. Le clic et le
   * sélecteur le prennent tout de suite, pour que le choix ne revienne pas en
   * arrière le temps d'un aller-retour ; les lignes de mesure attendent la
   * grille.
   */
  setSignature(signature: Signature): void {
    const current = this._continuousMetronome.signature.value;

    if (current.num === signature.num && current.den === signature.den) {
      return;
    }

    this._continuousMetronome.setSignature(signature);

    if (this.signatureProposer !== null) {
      this.signatureProposer(signature);
    }
  }

  /** Confie le chiffrage à une séance, ou le rend au studio avec `null`. */
  setSignatureProposer(proposer: ((signature: Signature) => void) | null): void {
    this.signatureProposer = proposer;
  }

  /**
   * Pose la grille d'une séance sur le transport, et son tempo et son chiffrage
   * sur ce qui les affiche et les bat.
   *
   * La vue revient à la vitesse 1 : la grille porte désormais le tempo, et une
   * vitesse locale en plus le compterait deux fois. `originalBpm` suit, pour
   * que le rapport affiché reste 1.
   */
  applySessionTempo(timeline: MusicalTimeline, bpm: number): void {
    this.transport.setMusicalTimeline(timeline);

    // Le métronome bat la mesure de la séance. Posé directement, et non par
    // `setSignature` : ce qui vient du service n'y retourne pas.
    const { numerator, denominator } = timeline.getTimeSignatureAtTick(0);

    this._continuousMetronome.setSignature({ num: numerator, den: denominator });

    // Une vitesse restée d'un ralenti en solo tiendrait la vue hors du direct,
    // et les notes des partenaires ne sonneraient plus à l'instant. Une vue qui
    // défile retourne au direct ; une vue figée — la séance est en pause —
    // garde sa place, et la reprise la rendra au direct.
    if (this.localTransport.getPlaybackRate() !== 1) {
      if (this.localTransport.isDetached) {
        this.localTransport.setPlaybackRate(1);
      } else {
        this.localTransport.followShared();
      }
    }

    this._originalBpm = bpm;
    this._continuousMetronome.setBpm(this.clickBpm(bpm));
    this._bpm.set(bpm);
  }

  get volume(): ReadonlyObservable<number> {
    return this._volume;
  }

  setVolume(volume: number): void {
    this._volume.set(volume);
  }

  get muted(): ReadonlyObservable<boolean> {
    return this._muted;
  }

  setMuted(muted: boolean): void {
    this._muted.set(muted);
  }

  get metronomeEnabled(): ReadonlyObservable<boolean> {
    return this._metronomeEnabled;
  }

  setMetronomeEnabled(enabled: boolean): void {
    if (this._metronomeEnabled.value === enabled) {
      return;
    }

    this._metronomeEnabled.set(enabled);

    // Start / stop the click in the right mode for the current transport
    // state. No longer force-plays: enabling the metronome while paused runs
    // the standalone (free) pulse so it clicks without pressing play, then
    // re-locks onto the playhead grid once playback starts.
    // Intentionally one-way: turning the metronome OFF does NOT pause.
    // Other consumers (rhythm-run, tempo-drill) drive `start/stop` on the
    // continuousMetronome instance directly, they don't flip this flag.
    this.applyMetronomeMode();

    this.setHighlightActiveBeat(enabled);
  }

  /**
   * Reconcile the continuous metronome's running mode with the current state
   * (metronome toggle, count-in, transport play/pause). Idempotent: only
   * restarts the scheduler when the desired mode actually changes.
   *
   * Free (paused) → synced (playing) transitions normally happen through a
   * count-in, which lands on the downbeat and bridges the two grids, so the
   * pulse stays phase-continuous across the handoff.
   */
  private applyMetronomeMode(): void {
    const cm = this._continuousMetronome;

    let desired: "stopped" | "free" | "synced";

    if (
      !this._metronomeEnabled.value
      || this._countInRunning
      || this._countIn.active
      // Le rideau d'un conseil de section : on lit, on ne joue pas.
      || this._sectionTips.showing
    ) {
      desired = "stopped";
    } else if (this.localTransport.isDetached) {
      desired = "free";
    } else {
      desired = "synced";
    }

    if (desired === this._metronomeRunMode) {
      return;
    }

    if (cm.isRunning) {
      cm.stop();
    }

    if (desired === "free") {
      cm.setMuted(false);
      cm.start(this.clickBpm(this._bpm.value));
    } else if (desired === "synced") {
      cm.setMuted(false);
      cm.startSynced(this.localTransport);
    }

    this._metronomeRunMode = desired;
  }

  /**
   * Restore a clean default musical timeline on the transport. Call when
   * returning to the empty studio (after the boot jingle or a song unload):
   * the jingle / last piece installs its own timeline via `setMusicalTimeline`
   * and nothing resets it, so the transport tempo would linger (e.g. the
   * jingle's 90 BPM) while the UI BPM stays at the studio default. Since the
   * synced metronome (and count-in, and note playback) follow the transport
   * timeline, that mismatch makes the click drift from the UI tempo. Resetting
   * realigns the transport tempo with the Studio defaults.
   */
  resetTimeline(): void {
    const ppq = this.transport.timeline.getPpq();
    const { num, den } = this._continuousMetronome.signature.value;

    // La signature choisie au métronome, et le tempo par défaut quelle qu'elle
    // soit : changer de signature en cours de jeu ne change que la grille. Un
    // autre tempo changerait le tick de chaque instant — les notes déjà jouées,
    // datées en ticks, sauteraient ailleurs, et le métronome calé attendrait
    // que la tête rattrape son dernier temps.
    this.ownTimeline = new MusicalTimeline(ppq, undefined, { numerator: num, denominator: den });
    this.transport.setMusicalTimeline(this.ownTimeline);
  }

  get tonality(): ReadonlyObservable<Tonality | null> {
    return this._tonality;
  }

  setTonality(tonality: Tonality | null): void {
    this._tonality.set(tonality);
  }

  get highlightActiveBeat(): ReadonlyObservable<boolean> {
    return this._highlightActiveBeat;
  }

  /** Live keyboard strip height (px), tracked by the React overlays. */
  get keyboardHeight(): ReadonlyObservable<number> {
    return this._keyboardHeight;
  }

  /** Largeur courante du rail de minimap (px), suivie par les overlays React. */
  get minimapWidth(): ReadonlyObservable<number> {
    return this._minimapWidth;
  }

  /** Distance courante playhead ↔ bas du roll (px), suivie par les overlays React. */
  get playheadDistance(): ReadonlyObservable<number> {
    return this._playheadDistance;
  }

  /** La pose des bornes est-elle ouverte au joueur ? */
  get recordZoneEditable(): ReadonlyObservable<boolean> {
    return this._recordZoneEditable;
  }

  /** Ouvrir ou fermer la pose des bornes dans la règle du roll. */
  setRecordZoneEditable(editable: boolean): void {
    this._recordZoneEditable.set(editable);
    this.pixiView?.setRecordZoneEditable(editable);
  }

  /** La zone d'enregistrement, en ticks, ou `null`. */
  get recordZone(): ReadonlyObservable<RecordZone | null> {
    return this._recordZone;
  }

  /**
   * Poser ou retirer la zone d'enregistrement.
   *
   * Le joueur la pose dans la règle du roll ; ceci est l'autre porte, pour
   * l'enlever ou la placer depuis un écran. Réappliquée quand une vue est
   * (re)construite, comme la plage travaillée.
   */
  setRecordZone(zone: RecordZone | null): void {
    this._recordZone.set(zone);
    this.pixiView?.setRecordZone(zone);
  }

  /**
   * Le délai de restitution, en millisecondes.
   *
   * La tête de lecture — ce qui se dessine, ce qui sonne — court de ce délai
   * derrière la position de séance ; la capture du clavier reste au présent
   * vrai. Un seul objet le porte, le transport local, et tout le monde le lui
   * demande : ni le clavier ni la vue n'ont leur propre nombre. La ligne du
   * présent que le roll dessine `D` au-dessus de la tête lit ce même délai à
   * chaque image — elle l'a reçu par une seconde entrée, alimentée par le même
   * appelant, et deux entrées qui disent la même chose finissent par diverger.
   * Une seule entrée place la tête *et* la ligne. Voir `LocalTransport` et
   * `now-line-offset.ts`.
   *
   * Le studio ne le décide pas : il vient de la séance. Il ne sait pas non
   * plus pourquoi il existe — un mode, un réglage — et n'a pas à le savoir.
   */
  setPlayoutDelayMs(delayMs: number): void {
    this.localTransport.setPlayoutDelay(delayMs / 1000);
    this.pixiView?.playoutDelayChanged();
  }

  /**
   * Ce poste émet-il ses gestes vers la séance.
   *
   * Décide où le clavier capte : au présent vrai quand on émet, à la tête de
   * lecture quand on écoute — voir `PianoControler.setEmitting`. Comme le
   * délai, le studio ne le décide pas et ne sait pas pourquoi : il vient de la
   * séance, qui lit qui mène dans l'état publié.
   */
  setLiveEmitting(emitting: boolean): void {
    this.pianoControler.setEmitting(emitting);
  }

  setHighlightActiveBeat(enabled: boolean): void {
    this._highlightActiveBeat.set(enabled);
  }

  /**
   * The passage the learning journey is working on, drawn on the roll with
   * its outside dimmed and its boundaries ruled. `null` clears it.
   *
   * Kept as plain state rather than an `Observable`: it changes at the pace
   * of a step and has exactly one consumer, the roll. It is re-applied when a
   * view is (re)built, like the beat grid above it.
   */
  setWorkRange(range: { startTick: number; endTick: number } | null): void {
    this._workRange = range;
    this.pixiView?.setWorkRange(range);
  }

  /**
   * The key spans of the loaded piece — where its sections depart from its
   * key and return to it — drawn on the roll's right edge. `[]` clears them.
   * Plain state re-applied when a view is (re)built, like the work range.
   */
  setKeySpans(spans: readonly KeySpan[]): void {
    this._keySpans = spans;
    this.pixiView?.setKeySpans(spans);
  }

  get workRange(): { startTick: number; endTick: number } | null {
    return this._workRange;
  }

  /**
   * How the live piano lane is drawn — as ordinary notes, hidden, or as the
   * **trace**: a contour over the written notes showing what was struck.
   *
   * Same shape as `setWorkRange` above, and for the same reasons: it changes
   * at the pace of a step, it has one consumer, and it is re-applied when a
   * view is rebuilt.
   */
  setLiveLaneMode(mode: LiveLaneMode): void {
    this._liveLaneMode = mode;
    this.pixiView?.setLiveLaneMode(mode);
  }

  /**
   * Fermer la navigation du rouleau au joueur, ou la lui rendre.
   *
   * Même forme que `setWorkRange` : retenu ici pour être reposé sur une vue
   * reconstruite, parce que la vue Pixi naît en asynchrone et peut renaître —
   * le spectateur qui entre avant qu'elle existe doit la trouver fermée. Le
   * studio ne sait pas pourquoi on ferme, et n'a pas à le savoir : ça vient
   * de la séance. Voir `ViewNavigator.setLocked`.
   */
  setNavigationLocked(locked: boolean): void {
    this._navigationLocked = locked;
    this.pixiView?.setNavigationLocked(locked);
  }

  get navigationLocked(): boolean {
    return this._navigationLocked;
  }

  get liveLaneMode(): LiveLaneMode {
    return this._liveLaneMode;
  }

  /**
   * How many strikes the live piano lane is holding right now.
   *
   * The one thing a caller needs before offering to show the trace: a pass
   * where nothing was played leaves an empty lane, and an offer that opens
   * onto nothing is worse than no offer. `getNotesCount` counts keys still
   * down as well as those released, so it is honest mid-pass too.
   */
  liveStrikeCount(): number {
    return this.pianoControler.strikeCount();
  }

  /**
   * Oublier ce que le joueur a déjà joué.
   *
   * La piste vive est cumulative par construction — en jeu libre, revoir ce
   * qu'on vient de jouer est précisément le service rendu. À chaque nouveau
   * contenu chargé, ça devient l'inverse : le gris du tracé se superpose à la
   * notation qu'on vient d'ouvrir, et on lit son propre passé au lieu de
   * l'exercice.
   */
  clearLivePiano(): void {
    this.pianoControler.clearLive();
    this.pixiView?.refreshClearedNotes();
  }

  get noteEffectStyle(): ReadonlyObservable<NoteEffectStyle> {
    return this._noteEffectStyle;
  }

  setNoteEffectStyle(style: NoteEffectStyle): void {
    this._noteEffectStyle.set(style);
  }

  get measureLinesVisible(): ReadonlyObservable<boolean> {
    return this._measureLinesVisible;
  }

  setMeasureLinesVisible(visible: boolean): void {
    this._measureLinesVisible.set(visible);
  }

  /**
   * Réserve une hauteur (px) au bas du roll, que la playhead remonte pour
   * surplomber. Posée par ce qui occupe la place, qui la mesure.
   */
  setFootReserve(px: number): void {
    this._footReserve.set(px);
  }

  /**
   * Le studio a-t-il fini son entrée ?
   *
   * Entrer dans le studio, c'est un carton d'accueil puis un jingle — qui joue
   * dans le rouleau, sur le transport, comme un morceau. Tant qu'il joue, la
   * place est prise : un morceau posé là sonnerait avec lui et lui volerait sa
   * tête de lecture. Ce qui se charge tout seul en arrivant — un replay de
   * récital, par exemple — attend donc ce moment.
   *
   * C'est la page qui le déclare : elle seule mène cette entrée, et sait quand
   * elle est finie, jingle passé, sauté, ou jamais joué.
   */
  get ready(): ReadonlyObservable<boolean> {
    return this._ready;
  }

  /** Le studio a fini son entrée. */
  markReady(): void {
    this._ready.set(true);
  }

  /**
   * L'entrée est à refaire : la page qui l'avait menée n'est plus montée.
   *
   * Le studio vit à la racine de l'application, la page qui mène son entrée
   * non. Ouvrir un replay la remonte (`ReplayProvider` l'enveloppe alors dans
   * sa séance), et la nouvelle rejoue carton et jingle. Une entrée finie par
   * l'ancienne laissait le replay se poser **pendant** ce jingle, dont la fin
   * rendait au transport la grille du studio vide : un récital enregistré à
   * 80 se relisait à 120. Constaté le 2026-09-21, en ouvrant le replay juste
   * après l'avoir enregistré.
   */
  markEntering(): void {
    this._ready.set(false);
  }

  /**
   * Appelle ce rappel quand le studio a fini son entrée — tout de suite s'il
   * l'avait déjà finie —, et rend de quoi se désabonner avant.
   */
  onReady(callback: () => void): () => void {
    if (this._ready.value) {
      callback();

      return () => {};
    }

    const stop = this._ready.subscribe((ready) => {
      if (!ready) {
        return;
      }

      stop();
      callback();
    });

    return stop;
  }

  /** Les notes partent-elles en paillettes ? Retenu pour la vue qui se rattache plus tard. */
  private noteDust = false;

  /**
   * Les notes partent en paillettes en passant la tête de lecture, ou la
   * traversent entières. Réglage de déploiement (`NOTE_DUST`, côté serveur),
   * pas une préférence : rien n'est persisté.
   */
  setNoteDust(enabled: boolean): void {
    this.noteDust = enabled;
    this.pixiView?.setNoteDust(enabled);
  }

  // ── Les sessions ─────────────────────────────────────────────

  /**
   * Ce que le studio prête à la session en scène.
   *
   * Une seule direction : la session emprunte l'appareil, l'appareil ne connaît
   * d'elle que ce que le gestionnaire lui en dit.
   */
  private buildSessionStage(): SessionStage {
    return {
      transport: this.transport,
      localTransport: this.localTransport,
      ticker: this.ticker,
      audioContext: this.ctx,
      showTrack: (track) => {
        this.trackManager.add(track);
        this.centerViewportOnTracks();
      },
      hideTrack: (track) => {
        this.trackManager.remove(track);
        this.centerViewportOnTracks();
      },
      routePiano: (lane) => this.pianoControler.useLane(lane),
    };
  }

  /**
   * Une session solo s'arrête quand la sortie audio s'arrête.
   *
   * Le studio ne fait que rapporter le fait ; c'est le **type** de session qui
   * décide quoi en faire — en séance, l'audio de l'un ne décide rien pour les
   * autres. Asymétrique : rien ne repart au retour du contexte, parce qu'un
   * contexte se suspend pour des raisons qui ne sont pas « la personne veut une
   * pause ».
   */
  private watchAudioOutput(): void {
    const onStateChange = () => {
      const active = this.sessions.getActive();

      // `!== "running"` et non `=== "suspended"` : Safari pose `interrupted`
      // en passant en arrière-plan, et c'est ce cas-là qui gèle une session.
      if (this.ctx.state !== "running") {
        active?.audioOutputStopped();

        return;
      }

      // Et le retour du son rouvre le gel qu'il avait posé. Sans cette
      // ligne, un passage en arrière-plan sur iPhone laissait le studio avec
      // une tête de lecture morte jusqu'au rechargement — voir
      // `StudioSession.audioOutputResumed`.
      active?.audioOutputResumed();
    };

    this.ctx.addEventListener?.("statechange", onStateChange);
    this.unsubscribeAudioState = () => {
      this.ctx.removeEventListener?.("statechange", onStateChange);
    };
  }

  getSessions(): SessionManager {
    return this.sessions;
  }

  // ── La séquence de morceaux ──────────────────────────────────

  /**
   * Les morceaux chargés, à la suite, et le courant. C'est par là qu'un
   * morceau entre dans le studio quand il est *le morceau* — celui de la
   * page, de la barre latérale, des parcours : `getSequence().replace(c)`
   * pour un seul, `set([...])` pour plusieurs. `addTrack` reste la porte de
   * ce qui n'en est pas un : le jingle, les partenaires d'une séance, et le
   * replay tant qu'il n'est pas branché sur la séquence.
   */
  getSequence(): SongSequence {
    return this.sequence;
  }

  /**
   * Ce que le studio prête à la séquence : monter et démonter des pistes dans
   * la session en scène, et prévenir la vue.
   *
   * Même dessin que `buildSessionStage`, une couche au-dessus : la séquence
   * ne touche pas à la session, elle demande.
   */
  private buildSequenceStage(): SequenceStage {
    return {
      transport: this.transport,
      localTransport: this.localTransport,
      staged: (controler) => {
        const session = this.getSession();
        const track = session.getTracks().find((candidate) => candidate.getControler() === controler) ?? null;

        if (track !== null) {
          this.sequenceHome = session;
        }

        return track;
      },
      mount: (controler) => {
        const session = this.getSession();
        this.sequenceHome = session;

        return session.addTrack(controler);
      },
      // Sur la session qui tient la piste, même hors scène : une session
      // garée démonte ses ordonnanceurs en partant, et il ne reste alors qu'à
      // rendre ou détruire la piste elle-même.
      release: (track) => {
        (this.sequenceHome ?? this.getSession()).releaseTrack(track);
      },
      remove: (track) => {
        (this.sequenceHome ?? this.getSession()).removeTrack(track);
      },
      changed: () => this.applySequenceLayout(),
    };
  }

  /**
   * Pose sur la vue ce que la séquence lui dit : le plancher de navigation —
   * le début de la séquence, en ticks du morceau courant — et l'invalidation
   * de sa mise en page. Rappelé quand une vue est (re)construite.
   */
  private applySequenceLayout(): void {
    this.pixiView?.setNavigationFloor(-this.sequence.currentOffset);
    this.pixiView?.sequenceChanged();
  }

  /** La session en scène. Il y en a toujours une. */
  getSession(): StudioSession {
    const active = this.sessions.getActive();

    if (active === null) {
      throw new Error("Aucune session en scène");
    }

    return active;
  }

  /**
   * Fait entrer une session en scène, et sortir celle qui y était.
   *
   * Le `Transport` ne change pas d'objet — seul son état est réécrit —, ce qui
   * laisse intacts tous les `Scheduler` et tous les `LocalTransport` qui le
   * tiennent.
   */
  activateSession(session: StudioSession): void {
    this.sessions.activate(session);
    // La séquence ne vit que dans la session où elle a monté ses pistes ;
    // voir `sequenceHome`.
    this.sequence.setSuspended(this.sequenceHome !== null && session !== this.sequenceHome);
    this.centerViewportOnTracks();
    this.pixiView?.refreshClearedNotes();
  }

  /** La lane du piano de la session en scène. */
  getPianoLane(): Lane | null {
    return this.sessions.getActive()?.getPianoLane() ?? null;
  }

  /** The live piano track of the session on stage. */
  getPianoTrack(): Track | undefined {
    return this.sessions.getActive()?.getPianoTrack() ?? undefined;
  }

  addTrack(controler: Controler): Track {
    return this.getSession().addTrack(controler);
  }

  /**
   * Tear down a track and everything tied to it: schedulers, controler, lanes.
   * Use this when swapping the loaded song (e.g. after a transposition) so
   * the old audio doesn't keep playing alongside the new one.
   */
  removeTrack(track: Track): void {
    this.getSession().removeTrack(track);
    this.centerViewportOnTracks();
  }

  private centerViewportOnTracks(): void {
    const pianoComponent = this.pixiView?.getPianoComponent();

    if (!pianoComponent) {
      return;
    }

    const robust = this.computeRobustTessitura();
    const strict = this.computeStrictRange();

    if (robust && strict) {
      // Robust span drives the zoom; strict span guarantees no note is clipped
      // out of the rendered window.
      pianoComponent.frameForPiece(robust.min, robust.max, strict.min, strict.max);
      // A window change alters the white-key count -> clamped key width ->
      // keyboard height; re-run the layout so the strip height follows.
      this.pixiView?.relayout();

      return;
    }

    pianoComponent.resetViewport();
    this.pixiView?.relayout();
  }

  /**
   * Absolute pitch span of every note across all tracks (strict min/max), used
   * to guarantee the rendered window contains every note. Returns null when
   * there are no notes.
   */
  private computeStrictRange(): { min: number; max: number } | null {
    let min = Infinity;
    let max = -Infinity;

    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        const range = lane.getNoteRange();

        if (!range) {
          continue;
        }

        min = Math.min(min, range.min);
        max = Math.max(max, range.max);
      }
    }

    if (min > max) {
      return null;
    }

    return { min, max };
  }

  /**
   * Robust pitch range used to align the key width: the span that covers all
   * but a small fraction of note occurrences at each extreme. Trimming these
   * tails stops a handful of outlier notes (a stray low/high accent) from
   * shrinking the whole keyboard so 95% of the piece loses a comfortable zoom.
   *
   * The trimmed notes are NOT dropped — the rendered window is widened to the
   * strict pitch span (see computeStrictRange / frameForPiece), so outliers
   * stay rendered and reachable by the auto-follow.
   * Returns null when there are no notes.
   */
  private computeRobustTessitura(): { min: number; max: number } | null {
    // Fraction of note occurrences trimmed from each extreme.
    const TAIL_FRACTION = 0.02;
    const counts = new Array<number>(128).fill(0);

    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        lane.accumulatePitchCounts(counts);
      }
    }

    let total = 0;

    for (let midi = 0; midi < counts.length; midi++) {
      total += counts[midi];
    }

    if (total === 0) {
      return null;
    }

    const tail = Math.floor(total * TAIL_FRACTION);

    let acc = 0;
    let min = 0;

    for (let midi = 0; midi < counts.length; midi++) {
      acc += counts[midi];

      if (acc > tail) {
        min = midi;
        break;
      }
    }

    acc = 0;
    let max = counts.length - 1;

    for (let midi = counts.length - 1; midi >= 0; midi--) {
      acc += counts[midi];

      if (acc > tail) {
        max = midi;
        break;
      }
    }

    return min <= max ? { min, max } : { min: max, max: min };
  }

  setAutoHintSelect(enabled: boolean): void {
    this.pixiView?.setAutoHintSelect(enabled);
  }

  get autoHintSelect(): boolean {
    return this.pixiView?.autoHintSelect ?? false;
  }

  /**
   * Highlight notes in the piano-roll. Notes outside the selection are
   * dimmed by the renderer. `maxOnsetTick` defaults to `tick`.
   */
  setHintSelection(midis: readonly number[], tick: number, maxOnsetTick?: number): void {
    this.pixiView?.setHintSelection(midis, tick, maxOnsetTick);
  }

  clearHintSelection(): void {
    this.pixiView?.clearHintSelection();
  }

  /** Effective visual tick (accounts for scroll animation offset). */
  getEffectiveTick(): number {
    return this.pixiView?.getEffectiveTick() ?? this.localTransport.currentTick();
  }

  /** Smoothly scroll the piano roll to a target tick (spring animation). */
  /**
   * Conduire la navigation du studio depuis une surface React.
   *
   * Le roll fait déjà ce geste, dans le canvas, par les mêmes quatre verbes.
   * Ce qui manquait pour qu'une autre surface le fasse aussi n'était pas un
   * second gestionnaire, c'était ce passage : `ViewNavigator` vit dans la vue
   * Pixi, et rien ne l'atteignait de l'extérieur.
   *
   * Conséquence voulue : la surface qui s'en sert et le roll bougent ensemble,
   * par construction, puisqu'ils déplacent le même objet.
   */
  beginViewGesture(): void {
    this.pixiView?.beginViewGesture();
  }

  updateViewOffset(deltaTicks: number): void {
    this.pixiView?.updateViewOffset(deltaTicks);
  }

  endViewGesture(immediate = false): void {
    this.pixiView?.endViewGesture(immediate);
  }

  wheelViewOffset(deltaTicks: number): void {
    this.pixiView?.wheelViewOffset(deltaTicks);
  }

  /**
   * Placer la tête de lecture, en tenant compte de ce que la vue est en train
   * de faire.
   *
   * À préférer à `getLocalTransport().seekToTick` partout où le déplacement
   * peut tomber pendant un défilement ou un geste, c'est-à-dire partout où
   * l'application déplace la tête toute seule. Voir
   * `ViewNavigator.seekWithoutJump`. Sans vue Pixi, c'est un seek ordinaire :
   * il n'y a alors pas d'image à ne pas faire sauter.
   */
  seekWithoutJump(tick: number): void {
    if (this.pixiView) {
      this.pixiView.seekWithoutJump(tick);

      return;
    }

    this.localTransport.seekToTick(tick);
  }

  animateScrollToTick(tick: number, durationMs?: number): Promise<void> {
    return this.pixiView?.animateScrollToTick(tick, durationMs) ?? Promise.resolve();
  }

  /**
   * Subscribe to the player scrolling the roll and letting go: drag, wheel,
   * or a click on the minimap. The tick is where the view came to rest.
   *
   * Silent while the application scrolls the roll itself — which it does on
   * every attack of a practice run — so a listener woken by this knows the
   * player chose the place. See `ViewNavigator.onScrub`.
   */
  onViewScrub(listener: (tick: number) => void): () => void {
    this.scrubListeners.add(listener);

    return () => {
      this.scrubListeners.delete(listener);
    };
  }

  // ─── Count-in ───

  /**
   * Start playback with an optional section tip, then a count-in.
   * Flow: Tip (if any) → Count-in → Play.
   *
   * @param opts.skipSectionTips When `true`, the section tip step is
   *   bypassed entirely. Used by the learning journey runs where the
   *   classic studio tips are not relevant. Defaults to `false`,
   *   existing callers (toolbar, tempo playback) keep their behavior.
   */
  async startWithCountIn(fromTick?: number, opts?: { skipSectionTips?: boolean }): Promise<void> {
    const skipSectionTips = opts?.skipSectionTips ?? false;

    // Cancel any running count-in
    this._countIn.cancel();

    // Animate scroll to the snapped bar
    const tick = this.snapToBar(fromTick ?? this.localTransport.currentTick());
    await this.animateScrollToTick(tick);

    // Abort if a new count-in was started during the animation
    if (this._countIn.active) {
      return;
    }

    // Show section tip if available (does not auto-resume)
    if (!skipSectionTips) {
      await this._sectionTips.showAtTick(tick);

      // Abort if a new count-in was started while tip was showing
      if (this._countIn.active) {
        return;
      }
    }

    // The count-in owns the clicks while it runs; silence the standalone
    // metronome pulse for its duration so they don't double up. The flag also
    // shields against the transient `pause` event the count-in emits while
    // arming itself (it would otherwise restart the free pulse).
    this._countInRunning = true;
    this.applyMetronomeMode();

    let completed = false;

    // Run the count-in (handles pause, seek, bpm/ts resolution internally)
    try {
      completed = await this._countIn.start(tick);
    } finally {
      this._countInRunning = false;
    }

    if (completed) {
      // `play()` emits a `play` event that re-locks the metronome onto the
      // playhead grid (synced mode) via `applyMetronomeMode`.
      this.localTransport.play();
    } else {
      // Cancelled/superseded: restore the standalone pulse if the metronome
      // is still enabled and the transport is back to paused.
      this.applyMetronomeMode();
    }
  }

  /** Immediately silence any scheduled or currently-sounding notes across all lanes. */
  stopAllScheduledNotes(): void {
    this.sessions.getActive()?.stopAllNotes();
  }

  private snapToBar(tick: number): number {
    const ticksPerBar = this.transport.timeline.getTicksPerBar(tick);

    return ticksPerBar > 0 ? tick - (tick % ticksPerBar) : tick;
  }

  /**
   * Arme (ou retire) le bouclage sur une plage de ticks.
   *
   * Ne lance pas la lecture : poser une boucle sur un morceau en pause
   * l'arme, elle prendra effet au prochain play. Le retour de boucle est
   * annoncé par un décompte d'une mesure, voir `onLoopWrap`.
   */
  setLoop(loop: TransportLoop | null): void {
    this.localTransport.setLoop(loop);
  }

  /**
   * Réponse au retour de boucle : un décompte d'une mesure, puis la lecture.
   *
   * On ne passe pas par `startWithCountIn`, qui anime le défilement et
   * affiche les conseils de section — deux choses qu'on ne veut pas à chaque
   * tour. Le `CountIn` gère lui-même pause, seek et résolution du tempo.
   */
  private async countInLoopWrap(tick: number): Promise<void> {
    this._countIn.cancel();

    this._countInRunning = true;
    this.applyMetronomeMode();

    let completed = false;

    try {
      completed = await this._countIn.start(tick);
    } finally {
      this._countInRunning = false;
    }

    if (completed) {
      this.localTransport.play();

      return;
    }

    this.applyMetronomeMode();
  }

  getLoop(): TransportLoop | null {
    return this.localTransport.getLoop();
  }

  /** Mesure courante, 1-based. Lisible avant la première frame. */
  getCurrentMeasure(): number {
    return this._currentMeasure;
  }

  /**
   * S'abonner au changement de mesure. Rend la fonction de désabonnement.
   *
   * Publié par la boucle de rendu, qui ne tourne que quand quelque chose
   * bouge : au repos, aucun timer ne tourne pour s'apercevoir qu'il ne se
   * passe rien.
   */
  onMeasureChange(callback: (measure: number) => void): () => void {
    this.measureListeners.add(callback);

    return () => {
      this.measureListeners.delete(callback);
    };
  }

  /**
   * Déclarer qu'un calque cache ces surfaces du canvas. Rend le retrait.
   *
   * Le jeton est l'identité du déclarant : deux écrans qui cachent la même
   * surface se retirent chacun sans effacer ce que l'autre affirme. Voir
   * `canvas-cover.ts` pour pourquoi on déclare au lieu de mesurer.
   */
  coverCanvas(token: object, surfaces: readonly StudioSurface[]): () => void {
    if (this.canvasCovers.hold(token, surfaces)) {
      this._hiddenSurfaces.set(this.canvasCovers.hidden);
    }

    return () => {
      if (this.canvasCovers.release(token)) {
        this._hiddenSurfaces.set(this.canvasCovers.hidden);
      }
    };
  }

  /** Ce qui est caché du canvas, à cet instant. */
  get hiddenSurfaces(): ReadonlyObservable<ReadonlySet<StudioSurface>> {
    return this._hiddenSurfaces;
  }

  /**
   * S'abonner au tick dessiné, image par image. Rend le désabonnement.
   *
   * Pour les vues React qui doivent se poser exactement là où le roll se pose :
   * elles lisent la même position, à la même image, et cessent donc de pouvoir
   * le contredire. Voir `PixiStudioView.onFrame` pour ce que ce tick vaut
   * pendant un geste, et `useStudioTick` pour la façon de s'y brancher sans
   * repasser par React à chaque image.
   *
   * L'abonnement tient même avant que la vue Pixi existe : elle est construite
   * en asynchrone, et un écran monté entre-temps ne doit pas avoir à réessayer.
   * Il ne se passe alors rien, ce qui est juste, puisque rien n'est dessiné.
   */
  onFrame(callback: (tick: number) => void): () => void {
    this.frameListeners.add(callback);

    return () => {
      this.frameListeners.delete(callback);
    };
  }

  private emitMeasure(measure: number): void {
    if (measure === this._currentMeasure) {
      return;
    }

    this._currentMeasure = measure;
    this.measureListeners.forEach((listener) => listener(measure));
  }

  /**
   * Monte la vue Pixi dans `container`.
   *
   * Une seule vue à la fois : rappelée alors qu'une vue existe — remontage du
   * conteneur, HMR —, elle démonte d'abord l'existante plutôt que de la
   * rendre. Rendre l'ancienne serait faux dès que le conteneur a changé : elle
   * dessinerait dans un nœud que React a retiré, et l'écran resterait vide.
   * Les appels sont sérialisés pour qu'un second, arrivé pendant le démarrage
   * asynchrone du premier, ne construise pas une deuxième application Pixi.
   */
  createView(container: HTMLElement): Promise<void> {
    const creation = this.viewCreation.then(() => this.buildView(container));

    this.viewCreation = creation.catch(() => undefined);

    return creation;
  }

  private async buildView(container: HTMLElement): Promise<void> {
    if (this.pixiView) {
      await this.pixiView.destroy();
      this.pixiView = undefined;
    }

    const view = new PixiStudioView(
      container,
      this.piano,
      this.localTransport,
      this.trackManager,
      // La session d'abord, qui connaît tout ce qui est en scène ; la
      // séquence ensuite, seule à savoir de quelle main est une lane d'un
      // morceau hors scène. Le second regard ne se paie que sur un échec du
      // premier, c'est-à-dire jamais avec un seul morceau.
      (lane) =>
        this.sessions.getActive()?.getLaneView(lane)?.colorToken
        ?? this.sequence.laneViewOf(lane)?.colorToken
        ?? 'default',
      (height) => this._keyboardHeight.set(height),
      (width) => this._minimapWidth.set(width),
      (distance) => this._playheadDistance.set(distance),
      this.sequence,
    );
    await view.start();

    view.build();

    this.unsubscribeMeasure?.();
    this.unsubscribeMeasure = view.onMeasureChange((measure) =>
      this.emitMeasure(measure),
    );

    this.unsubscribeFrame?.();
    this.unsubscribeFrame = view.onFrame((tick) => {
      this.frameListeners.forEach((listener) => listener(tick));
    });

    this.unsubscribeScrub?.();
    this.unsubscribeScrub = view.onScrub((tick) => {
      this.scrubListeners.forEach((listener) => listener(tick));
    });

    this.unsubscribeRecordZone?.();
    this.unsubscribeRecordZone = view.onRecordZoneChange((zone) => {
      this._recordZone.set(zone);
    });

    // La vue est construite en asynchrone : un écran monté entre-temps a déjà
    // déclaré ce qu'il cache, et cette déclaration serait perdue sans ce
    // rattrapage.
    view.setHiddenSurfaces(this._hiddenSurfaces.value);
    view.setHighlightActiveBeat(this._highlightActiveBeat.value);
    view.setWorkRange(this._workRange);
    view.setKeySpans(this._keySpans);
    view.setLiveLaneMode(this._liveLaneMode);
    view.setNavigationLocked(this._navigationLocked);
    view.setRecordZone(this._recordZone.value);
    view.setRecordZoneEditable(this._recordZoneEditable.value);
    view.setNoteDust(this.noteDust);
    view.setMeasureLinesVisible(this._measureLinesVisible.value);
    view.setFootReserve(this._footReserve.value);
    this.pixiView = view;
    this.applyEffectiveNoteEffectStyle();
    // Une séquence remplie avant la vue lui donne son plancher maintenant.
    this.applySequenceLayout();
  }

  async destroy() {
    this.unsubscribePerf?.();
    this.unsubscribePerf = null;

    this.unsubscribeTransportEvents?.();
    this.unsubscribeTransportEvents = null;

    this.unsubscribeLoopWrap?.();
    this.unsubscribeLoopWrap = null;

    this.unsubscribeSectionTips?.();
    this.unsubscribeSectionTips = null;

    this.unsubscribeMeasure?.();
    this.unsubscribeMeasure = null;

    this.unsubscribeFrame?.();
    this.unsubscribeFrame = null;

    this.unsubscribeScrub?.();
    this.unsubscribeScrub = null;

    this.unsubscribeRecordZone?.();
    this.unsubscribeRecordZone = null;

    this.measureListeners.clear();
    this.frameListeners.clear();
    this.scrubListeners.clear();

    this.unsubscribeAudioState?.();
    this.unsubscribeAudioState = null;

    // Le piano est un singleton de module (`standard88Keys`) : une touche
    // encore enfoncée survivrait au studio suivant.
    this.piano.allNotesOff();

    this.pianoControler.destroy();

    // Avant les sessions : ce que la séquence tient hors scène n'a qu'elle
    // pour être détruit, ce qu'elle a monté part avec sa session.
    this.sequence.dispose();

    // Les sessions emportent leurs ordonnanceurs, leurs pistes, leurs lanes et
    // leurs synthés — c'est là que vit la matière depuis qu'elle a quitté le
    // studio.
    this.sessions.dispose();

    // Destroy view (Pixi application, canvas, WebGL context)
    if (this.pixiView) {
      await this.pixiView.destroy();
      this.pixiView = undefined;
    }

    // Les composants Pixi s'y étaient abonnés ; ils sont partis avec la vue.
    this.trackManager.dispose();

    // Les calques montés ont pu ne pas se retirer avant le démontage.
    this.canvasCovers = new CanvasCovers();

    this.disposables.forEach((d) => d.dispose());
    this.disposables.length = 0;

    // Destroy local transport (unregisters from clock)
    this.localTransport.destroy();

    // Stop clock (clears setTimeout loop)
    this.ticker.destroy();

    // The AudioContext is owned by the React context provider. It is closed
    // there after this Studio instance has been destroyed.
  }
}
